import { useCallback, useEffect, useState } from 'react';

// Mantiene una única instancia de carga por cada `src`
const scriptRegistry = new Map();

/**
 * Hook para cargar un script de forma perezosa (lazy).
 *
 * - Recibe un `src`.
 * - NO inicia la carga automáticamente.
 * - Expone `{ isScriptLoaded, isScriptError, loadScript }`.
 * - Garantiza una única instancia del script por `src`.
 */
const ensureScriptEntry = (src) => {
  if (!scriptRegistry.has(src)) {
    scriptRegistry.set(src, {
      status: 'idle', // 'idle' | 'loading' | 'loaded' | 'error'
      promise: null,
      refCount: 0 // Contador de referencias
    });
  }

  return scriptRegistry.get(src);
};

const createOrAttachScript = (src, entry) => {
  if (typeof document === 'undefined') {
    // Entorno sin DOM (SSR, tests, etc.)
    entry.status = 'error';
    entry.promise = Promise.reject(new Error('Document is not available'));
    return entry.promise;
  }

  // Si ya hay un script con ese src, reutilizarlo y escuchar sus eventos.
  let script = document.querySelector(`script[src="${src}"]`);

  entry.status = 'loading';

  entry.promise =
    entry.promise ||
    new Promise((resolve, reject) => {
      const handleLoad = () => {
        entry.status = 'loaded';
        resolve();
      };

      const handleError = () => {
        entry.status = 'error';
        reject(new Error(`Failed to load script: ${src}`));
      };

      if (!script) {
        script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener('load', handleLoad);
        script.addEventListener('error', handleError);
        document.body.appendChild(script);
      } else {
        // Ya existe el script en el DOM.
        if (script.getAttribute('data-loaded') === 'true') {
          entry.status = 'loaded';
          resolve();
        } else if (script.getAttribute('data-error') === 'true') {
          entry.status = 'error';
          reject(new Error('Failed to load script: ' + src));
        } else {
          script.addEventListener('load', handleLoad);
          script.addEventListener('error', handleError);
        }
      }

      // Marcar estado en el DOM cuando termine
      script.addEventListener('load', () => {
        script.setAttribute('data-loaded', 'true');
      });
      script.addEventListener('error', () => {
        script.setAttribute('data-error', 'true');
      });
    });

  return entry.promise;
};

export const useScript = (src) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isScriptError, setIsScriptError] = useState(false);

  // Incrementar contador de referencias al montar y limpiar al desmontar
  useEffect(() => {
    if (!src) return;

    const entry = ensureScriptEntry(src);
    entry.refCount += 1;

    // Sincronizar estado local si otro componente ya cargó el script.
    if (entry.status === 'loaded') {
      setIsScriptLoaded(true);
      setIsScriptError(false);
    } else if (entry.status === 'error') {
      setIsScriptLoaded(false);
      setIsScriptError(true);
    }

    // Cleanup: decrementar contador y limpiar si no hay más referencias
    return () => {
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        scriptRegistry.delete(src);
      }
    };
  }, [src]);

  const loadScript = useCallback(() => {
    if (!src) return Promise.reject(new Error('src is required'));

    const entry = ensureScriptEntry(src);

    if (entry.status === 'loaded') {
      setIsScriptLoaded(true);
      setIsScriptError(false);
      return Promise.resolve();
    }

    if (entry.status === 'error') {
      setIsScriptLoaded(false);
      setIsScriptError(true);
      return Promise.reject(new Error(`Failed to load script: ${src}`));
    }

    // En este punto: 'idle' o 'loading'
    setIsScriptLoaded(false);
    setIsScriptError(false);

    const promise = createOrAttachScript(src, entry);

    promise
      .then(() => {
        setIsScriptLoaded(true);
        setIsScriptError(false);
      })
      .catch(() => {
        setIsScriptLoaded(false);
        setIsScriptError(true);
      });

    return promise;
  }, [src]);

  return { isScriptLoaded, isScriptError, loadScript };
}

